import React, { useState, useRef, useEffect } from 'react';
import YouTube from 'react-youtube';
import Webcam from 'react-webcam';
import SequenceSelector from './components/SequenceSelector';
import './styles.css';

const App = () => {
  const [videoUrl, setVideoUrl] = useState('https://www.youtube.com/watch?v=MP4A3cLuBP0');
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [start, setStart] = useState(0);
  const [segmentDuration, setSegmentDuration] = useState(20);
  const [isLooping, setIsLooping] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [videos, setVideos] = useState([
    { id: 'dQw4w9WgXcQ', title: 'Mime 1', url: 'https://www.youtube.com/watch?v=MP4A3cLuBP0' },
    { id: 'kJQP7kiw5Fk', title: 'Mime 2', url: 'https://www.youtube.com/watch?v=FPMBV3rd_hI' },
  ]);
  const [videoSize, setVideoSize] = useState({ width: 640, height: 390 });
  const [webcamSize, setWebcamSize] = useState({ width: 640, height: 390 });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const videoRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const loadCastApi = () => {
      const script = document.createElement('script');
      script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
      script.onload = initializeCastApi;
      document.body.appendChild(script);
    };

    const initializeCastApi = () => {
      const context = window.cast.framework.CastContext.getInstance();
      context.setOptions({
        receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });

      context.addEventListener(window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED, (event) => {
        if (event.sessionState === window.cast.framework.SessionState.SESSION_STARTED) {
          loadMedia();
        }
      });
    };

    window['__onGCastApiAvailable'] = (isAvailable) => {
      if (isAvailable) {
        loadCastApi();
      }
    };
  }, []);

  const loadMedia = () => {
    const session = window.cast.framework.CastContext.getInstance().getCurrentSession();
    const mediaInfo = new window.chrome.cast.media.MediaInfo(videoUrl, 'video/mp4');
    const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
    session.loadMedia(request).then(
      () => { console.log('Load succeed'); },
      (errorCode) => { console.log('Error code: ' + errorCode); }
    );
  };

  const handleCastClick = () => {
    const context = window.cast.framework.CastContext.getInstance();
    context.requestSession().then(
      () => { console.log('Session started'); },
      (errorCode) => { console.log('Error starting session: ' + errorCode); }
    );
  };

  const handleVideoReady = (event) => {
    videoRef.current = event.target;
    setIsVideoReady(true);
    videoRef.current.seekTo(start, true);
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        clearInterval(intervalRef.current);
        videoRef.current.pauseVideo();
      } else {
        clearInterval(intervalRef.current);
        videoRef.current.seekTo(start, true);
        videoRef.current.playVideo();
        intervalRef.current = setInterval(() => {
          if (videoRef.current.getCurrentTime() >= start + segmentDuration) {
            if (isLooping) {
              videoRef.current.seekTo(start, true);
            } else {
              videoRef.current.pauseVideo();
              clearInterval(intervalRef.current);
            }
          }
        }, 100);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSpeedChange = (event) => {
    const speed = parseFloat(event.target.value);
    setPlaybackRate(speed);
    if (videoRef.current) {
      videoRef.current.setPlaybackRate(speed);
    }
  };

  const handleSelectVideo = (url) => {
    clearInterval(intervalRef.current);
    setVideoUrl(url);
    setIsVideoReady(false);
    setStart(0);
    setSegmentDuration(20);
  };

  const handleFullScreen = () => {
    if (isFullScreen) {
      setVideoSize({ width: 640, height: 390 });
      setWebcamSize({ width: 640, height: 390 });
    } else {
      const screenHeight = window.innerHeight;
      const newWidth = screenHeight * (640 / 390);

      setVideoSize({ width: newWidth, height: screenHeight });
      setWebcamSize({ width: newWidth, height: screenHeight });
    }
    setIsFullScreen(!isFullScreen);
  };

  const handleAddVideo = () => {
    const videoId = extractVideoId(newVideoUrl);
    if (videoId) {
      const newVideo = {
        id: videoId,
        title: `Video ${videos.length + 1}`,
        url: `https://www.youtube.com/watch?v=${videoId}`,
      };
      setVideos([...videos, newVideo]);
      setNewVideoUrl('');
      handleSelectVideo(`https://www.youtube.com/watch?v=${videoId}`);
    } else {
      alert('URL YouTube invalide. Veuillez entrer une URL valide.');
    }
  };

  const extractVideoId = (url) => {
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  useEffect(() => {
    if (isVideoReady && videoRef.current) {
      videoRef.current.seekTo(start, true);
    }
  }, [start, segmentDuration]);

  const handleUpdateTimes = () => {
    if (videoRef.current) {
      videoRef.current.seekTo(start, true);
      videoRef.current.pauseVideo();
    }
  };

  const adjustSegmentDuration = (amount) => {
    setSegmentDuration(prevDuration => {
      const newDuration = Math.max(5, Math.min(prevDuration + amount, 30));
      return newDuration;
    });
  };

  const opts = {
    height: videoSize.height,
    width: videoSize.width,
    playerVars: {
      autoplay: 0,
      controls: 1,
    },
  };

  return (
    <div className={`container ${isFullScreen ? 'full-screen' : ''}`}>
      <div className={`header ${isFullScreen ? 'hidden' : ''}`}>
        <div className="menu">
          <a href="https://www.bandeimprovisation.com/">Accueil</a>
          <a href="https://www.bandeimprovisation.com/outils-pour-impro">Outils pour impro</a>
        </div>
      </div>
  
      <div className="video-container" style={{ position: 'relative', width: videoSize.width, height: videoSize.height }}>
        <YouTube
          videoId={new URL(videoUrl).searchParams.get('v')}
          opts={opts}
          onReady={handleVideoReady}
        />
        <Webcam
          audio={false}
          mirrored={true}
          className="webcam-overlay"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: webcamSize.width,
            height: webcamSize.height,
            opacity: 0.5
          }}
        />
      </div>
  
      <div className="controls">
        <button onClick={handlePlayPause} disabled={!isVideoReady}>
          {isPlaying ? 'Pause' : 'Jouer'}
        </button>
        <button onClick={() => setIsLooping(!isLooping)}>
          {isLooping ? 'Désactiver la boucle' : 'Activer la boucle'}
        </button>
        <button onClick={handleFullScreen}>
          {isFullScreen ? 'Quitter le plein écran' : 'Plein écran'}
        </button>
      </div>
  
      <div>
        <label>
          Vitesse:
          <select value={playbackRate} onChange={handleSpeedChange}>
            <option value="0.25">0.25x</option>
            <option value="0.5">0.5x</option>
            <option value="0.75">0.75x</option>
            <option value="1">1x</option>
          </select>
        </label>
      </div>
  
      <div>
        <SequenceSelector
          duration={videoRef.current ? videoRef.current.getDuration() : 0}
          startTime={start}
          segmentDuration={segmentDuration}
          onChange={({ start, end }) => {
            setStart(start);
            setSegmentDuration(end - start);
          }}
        />
      </div>
  
      <div className="segment-controls">
        <button onClick={() => adjustSegmentDuration(5)}>+5s</button>
        <button onClick={() => adjustSegmentDuration(-5)}>-5s</button>
      </div>
  
      <div className="youtube-url">
        <label>
          Ajouter une vidéo YouTube:
          <input
            type="text"
            placeholder="URL de la vidéo YouTube"
            value={newVideoUrl}
            onChange={(e) => setNewVideoUrl(e.target.value)}
          />
          <button onClick={handleAddVideo}>Ajouter</button>
        </label>
      </div>
  
      <div>
        <ul>
          {videos.map((video, index) => (
            <li key={index} onClick={() => handleSelectVideo(video.url)}>
              {video.title}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
  


  }
export default App;  